import numeral from 'numeral';
import moment from 'moment';
import { isNil, isNaN, get } from 'lodash';
import { yearMonthDayFormat } from '@enums/date-formats';
import CustomNumberCellEditor from '../pages/inputs/engine-inputs/localisedNumberCellEditor';
import agGridUtils from './ag-grid-utils';

// TODO: PRICE-4643 Refactor this to use the same formatting as the rest of the app, maybe it should rely on formatNumber like we do in the grid view
//       Should investigate how we should handle the custom locale dependent formatters in our app

// Validations
const lessThanOrEqual100 = {
  message: 'lessThanOrEqual100',
  test({ value } = {}) {
    if (isNil(value)) return true;

    return value <= 100;
  },
};
const lessThan100 = {
  message: 'lessThan100',
  test({ value } = {}) {
    if (isNil(value)) return true;

    return value < 100;
  },
};

const greaterThan0 = {
  message: 'greaterThan0',
  test({ value } = {}) {
    if (isNil(value)) return true;

    return value > 0;
  },
};

const greaterThanOrEqual0 = {
  message: 'greaterThanOrEqual0',
  test({ value } = {}) {
    if (isNil(value)) return true;

    return value >= 0;
  },
};

const mustBeValidNumber = {
  message: 'mustBeValidNumber',
  test({ value } = {}) {
    return !isNaN(value);
  },
};

const validations = {
  mustBeValidNumber,
  lessThanOrEqual100,
  lessThan100,
  greaterThan0,
  greaterThanOrEqual0,
};

// [] brackets make it optional
const fmtDecimalOptionalMantissa = '0[.][00]';

function getFormatStringFromConfig(numberFormatConfig) {
  if (!numberFormatConfig) return fmtDecimalOptionalMantissa;

  let formatString = '0';

  // Generate format string based on maximumFractionDigits
  const maxDigits = get(numberFormatConfig, 'maximumFractionDigits', 2);
  const minDigits = get(numberFormatConfig, 'minimumFractionDigits', 2);
  const optionalDigits = maxDigits - minDigits;

  // Create format string with fixed number of decimal places
  if (minDigits > 0) {
    formatString += '.';
    formatString += `${'0'.repeat(minDigits)}`;
  }

  if (optionalDigits > 0) {
    if (!formatString.includes('.')) {
      formatString += '[.]';
    }
    formatString += `[${'0'.repeat(optionalDigits)}]`;
  }

  return formatString;
}

function localisedFormatter(params) {
  if (isNaN(params.value)) {
    return params.value;
  }

  if (isNil(params.value)) {
    return;
  }

  const { value } = params;

  // Default to standard format
  let formatString = fmtDecimalOptionalMantissa;

  // Check for specific number format
  const numberFormat = get(params, 'colDef.context.numberFormatsConfig');
  if (numberFormat) {
    // Get format string dynamically from configuration
    formatString = getFormatStringFromConfig(numberFormat);
  }

  const res = numeral(value).format(formatString);
  return res;
}

const localisedNumericColFilterParams = {
  ...agGridUtils.getNumericFilterParams(),
  // Preserve any custom behavior specific to this module
  valueFormatter(val) {
    // This maintains backward compatibility with printColumnFilterValue
    if (isNil(val)) return '';
    return numeral(val).format(fmtDecimalOptionalMantissa);
  },
};

// This is the base number editor, setting the editor and localised inputs
const localisedNumericCol = {
  cellEditor: CustomNumberCellEditor,
  editable: true, // TODO: only allow editing if enabled is true
  valueFormatter: localisedFormatter,
  filter: 'agNumberColumnFilter',
  filterParams: localisedNumericColFilterParams,
  // Needs to be false to prevent exporting all formats like percentages
  // Necessary to parse pasted cells
  valueParser(params) {
    if (params.newValue) {
      // This is triggered by paste operations. We parse the string value according to locale.
      const res = numeral(params.newValue).value();
      return res;
    }

    return params.value;
  },
};

/**
 * Try to parse strictly using client date format.
 * If that fails, fallback to built in permissive parsing.
 * @param {object} params - AG Grid value parser params.
 * @returns {string|null} A YYYY-MM-DD string, or null if the value cannot be parsed.
 */
function localisedDateValueParser({ params, clientDateFormatForMoment }) {
  if (!params.newValue) return null;
  const strict = moment(params.newValue, clientDateFormatForMoment, true);
  if (strict.isValid()) return strict.format(yearMonthDayFormat);
  const fallback = moment(params.newValue);
  // Must return a YYYY-MM-DD string (not the raw moment object) so that downstream
  // JSON serialisation and the server's strict "YYYY-MM-DD" parsing (used to derive
  // e.g. overridePrice.expiryTimestamp) don't silently corrupt/NaN the stored value.
  return fallback.isValid() ? fallback.format(yearMonthDayFormat) : null;
}

// TODO: PRICE-3593 standardise valueFormatter and cellEditorParams across all date columns
// Some complexity comes from date cols which get converted to unix timestamps.
// e.g. overridePrice.effectiveDate -> overridePrice.effectiveTimestamp
const localisedDateCol = {
  cellEditor: 'datePickerEditor',
  cellEditorPopup: true,
  cellEditorPopupPosition: 'under',
  filter: 'agDateColumnFilter',
  filterParams: {
    comparator: (filterLocalDateAtMidnight, cellValue) => {
      if (cellValue == null) return -1;
      const cellDate = moment(cellValue, yearMonthDayFormat);
      const filterDate = moment(
        filterLocalDateAtMidnight.toISOString().split('T')[0],
        yearMonthDayFormat
      ).add({ days: 1 });
      if (filterDate.isSame(cellDate)) {
        return 0;
      }
      if (cellDate.isBefore(filterDate)) {
        return -1;
      }
      if (cellDate.isAfter(filterDate)) {
        return 1;
      }
    },
    browserDatePicker: true,
    minValidYear: 2000,
  },
};

const getCostFormatNumericCol = (numberFormatsConfig = null) => {
  // Create custom filter params with access to the given config
  const customFilterParams = {
    ...agGridUtils.getNumericFilterParams(),
    valueFormatter(val) {
      if (isNil(val)) return '';

      // Use either provided config or fallback
      if (numberFormatsConfig) {
        const formatString = getFormatStringFromConfig(numberFormatsConfig);
        return numeral(val).format(formatString);
      }

      return numeral(val).format(fmtDecimalOptionalMantissa);
    },
  };

  return {
    ...localisedNumericCol,
    context: { numberFormatsConfig }, // Store config in column context
    filterParams: customFilterParams,
  };
};

const percentageNumericCol = {
  ...localisedNumericCol,
  valueFormatter: localisedFormatter,
  filterParams: {
    ...localisedNumericColFilterParams,
    valueFormatter: localisedFormatter,
  },
};

export {
  validations,
  localisedNumericCol,
  localisedDateCol,
  localisedDateValueParser,
  percentageNumericCol,
  getCostFormatNumericCol,
  getFormatStringFromConfig,
  localisedFormatter,
};
